import React from 'react';
import { Alert, App, ConfigProvider } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Tabs from '../../../components/Tabs';
// 가장 최근 업무 화면(결제 설정 · PG 수수료)과 같은 톤을 쓴다 — 컨트롤 크기·여백·탭 모양
import { SettingsWorkspaceStyle } from '../../pgProviderRoute/common/styles';
import { ruleDetailUrl } from '../utils/routes';
import { SearchDictionaryStyle } from './styles';
import { SynonymHistoryPanel } from './SynonymHistoryPanel';
import { SynonymListPanel } from './SynonymListPanel';
import { UserDictionaryPanel } from './UserDictionaryPanel';

export type SearchDictionaryPermissions = {
  /** 상세 조회 — 이력 · 사용자사전 · 검증 · 미리보기 API 가 read 를 요구한다. 없으면 그 탭과 빠른 편집을 숨긴다 */
  canRead: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
};

/** 동의어가 기본 탭이다 — 목록 상태가 URL 쿼리를 통째로 쓰기 때문에 `tab` 없이 둔다 */
const TAB_KEYS = ['synonyms', 'user-dictionary', 'history'] as const;
type TabKey = (typeof TAB_KEYS)[number];
const TAB_TITLE: Record<TabKey, string> = { synonyms: '동의어', 'user-dictionary': '사용자사전', history: '변경 이력' };

export const SEARCH_DICTIONARY_THEME = {
  token: {
    colorPrimary: '#7950f2',
    borderRadius: 4,
    colorText: '#283141',
    colorTextSecondary: '#626e7f',
    controlHeight: 36,
    controlHeightSM: 28,
  },
  components: { Button: { fontWeight: 500, paddingInline: 16, paddingInlineSM: 10 } },
};

/**
 * 검색 사전 (AMZUWEB-694 · 백엔드 AMZBM-113) — 동의어 · 사용자사전 · 변경 이력.
 * 화면 구성은 설계 S3-D: 목록 + 빠른 편집 모달 + 규칙 상세 페이지.
 */
export const SearchDictionaryWorkspace = (props: SearchDictionaryPermissions): React.JSX.Element => {
  const { canRead, canCreate, canUpdate, canDelete } = props;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  // 목록 권한만 있으면 동의어 탭만 연다 — 나머지 탭은 read 권한이 있어야 서버가 답한다
  const visibleTabs: readonly TabKey[] = canRead ? TAB_KEYS : ['synonyms'];
  const activeTab: TabKey = visibleTabs.includes(rawTab as TabKey) ? (rawTab as TabKey) : 'synonyms';
  const canRollback = canCreate && canUpdate && canDelete;
  // 등록 화면 경로 권한(pathPermissions: create + read)과 같은 조건
  const canCreateRule = canCreate && canRead;

  return (
    <ConfigProvider theme={SEARCH_DICTIONARY_THEME}>
      <App>
        <SettingsWorkspaceStyle>
          <SearchDictionaryStyle>
            {!canUpdate && (
              <Alert
                className="settings-inline-alert"
                type="info"
                showIcon
                message="조회 권한으로 보고 있습니다. 동의어를 고치려면 수정 권한이 필요합니다."
              />
            )}
            <Tabs
              activeKey={visibleTabs.indexOf(activeTab)}
              onChange={(key: number) => {
                const next = visibleTabs[key] ?? 'synonyms';
                // 동의어 탭으로 돌아오면 쿼리를 비운다 — 다른 탭의 값이 목록 필터로 섞이지 않게
                setSearchParams(next === 'synonyms' ? {} : { tab: next }, { replace: true });
              }}
              destroyInactiveTabPane
              list={visibleTabs.map((tab, index) => ({
                id: index,
                title: TAB_TITLE[tab],
                content:
                  activeTab !== tab ? null : tab === 'synonyms' ? (
                    <SynonymListPanel canRead={canRead} canCreate={canCreateRule} canUpdate={canUpdate} />
                  ) : tab === 'user-dictionary' ? (
                    <UserDictionaryPanel />
                  ) : (
                    <SynonymHistoryPanel
                      canRollback={canRollback}
                      onOpenRule={(ruleId) => navigate(ruleDetailUrl(ruleId))}
                    />
                  ),
              }))}
            />
          </SearchDictionaryStyle>
        </SettingsWorkspaceStyle>
      </App>
    </ConfigProvider>
  );
};
